/**
 * Custom sidebar component for the new Backstage frontend system.
 * Renders the IntelliPact Admin Portal sidebar with logo, environment badge,
 * and navigation items from all registered plugins.
 *
 * @packageDocumentation
 */

import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/frontend-plugin-api';
import type {
  NavContentComponentProps,
  NavContentNavItem,
} from '@backstage/plugin-app-react';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';

// Import logo components
import LogoFull from '../Root/LogoFull';
import LogoIcon from '../Root/LogoIcon';

/**
 * Environment configuration mapping for the badge.
 * Combines color and labels into a single config object.
 */
const environmentConfig: Record<
  string,
  { color: string; full: string; short: string }
> = {
  development: { color: '#4caf50', full: 'Development', short: 'DEV' },
  test: { color: '#2196f3', full: 'Test', short: 'TEST' },
  staging: { color: '#ff9800', full: 'Staging', short: 'STG' },
  production: { color: '#f44336', full: 'Production', short: 'PROD' },
};

/**
 * Container for the sidebar logo with centered layout.
 */
const LogoContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px 12px',
  minHeight: 64,
});

/**
 * Container for the environment badge with centered layout.
 */
const BadgeContainer = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: 8,
  marginTop: -8,
});

/**
 * Environment label with styling for visibility.
 */
const EnvironmentLabel = styled('span')({
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '2px 8px',
  borderRadius: '4px',
  color: '#fff',
});

/**
 * Environment badge component showing current environment.
 */
const EnvironmentBadge = () => {
  const { isOpen } = useSidebarOpenState();
  const configApi = useApi(configApiRef);
  const env = (
    configApi.getOptionalString('auth.environment') || 'development'
  ).toLowerCase();
  const { color, full, short } =
    environmentConfig[env] || environmentConfig.development;

  return (
    <BadgeContainer>
      <EnvironmentLabel style={{ backgroundColor: color }}>
        {isOpen ? full : short}
      </EnvironmentLabel>
    </BadgeContainer>
  );
};

/**
 * Logo component for the sidebar.
 */
const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();

  return <LogoContainer>{isOpen ? <LogoFull /> : <LogoIcon />}</LogoContainer>;
};

/**
 * Special routes that need specific positioning in the sidebar.
 */
const SPECIAL_ROUTES = {
  HOME: '/',
  SEARCH: '/search',
  SETTINGS: '/settings',
  ADMIN_CONFIG: '/admin-config',
} as const;

/**
 * Avatar icon wrapper for the Settings sidebar item.
 * Renders the user's avatar when signed in, generic icon otherwise.
 */
const SettingsAvatarIcon = () => <UserSettingsSignInAvatar />;

const InferredNavSidebarItem = ({ href, icon, title }: NavContentNavItem) => {
  const Icon = () => icon;

  return <SidebarItem icon={Icon} to={href} text={title} />;
};

/**
 * Custom sidebar content component for the new frontend system.
 * Receives nav items from all plugins and renders them with custom branding.
 */
export const SidebarContent = ({ navItems }: NavContentComponentProps) => {
  const items = navItems.rest();

  // Extract special items that need specific positioning
  const homeItem = items.find(item => item.href === SPECIAL_ROUTES.HOME);
  const searchItem = items.find(item => item.href === SPECIAL_ROUTES.SEARCH);
  const settingsItem = items.find(
    item => item.href === SPECIAL_ROUTES.SETTINGS,
  );
  const adminConfigItem = items.find(
    item => item.href === SPECIAL_ROUTES.ADMIN_CONFIG,
  );

  // Filter out items that are rendered in specific positions
  const specialRoutes: string[] = Object.values(SPECIAL_ROUTES);
  const pluginItems = items.filter(item => !specialRoutes.includes(item.href));

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <EnvironmentBadge />
        <SidebarDivider />
        {/* Search at top */}
        {searchItem ? (
          <InferredNavSidebarItem {...searchItem} />
        ) : (
          <SidebarItem
            icon={SearchIcon}
            to={SPECIAL_ROUTES.SEARCH}
            text="Search"
          />
        )}
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Home item */}
          {homeItem ? (
            <InferredNavSidebarItem {...homeItem} />
          ) : (
            <SidebarItem icon={HomeIcon} to={SPECIAL_ROUTES.HOME} text="Home" />
          )}
          <SidebarDivider />
          {/* Render all plugin nav items */}
          <SidebarScrollWrapper>
            {pluginItems.map(item => (
              <InferredNavSidebarItem key={item.href} {...item} />
            ))}
          </SidebarScrollWrapper>
        </SidebarGroup>
        {/* Admin Config and Settings pinned to bottom */}
        <SidebarSpace />
        <SidebarDivider />
        {adminConfigItem && <InferredNavSidebarItem {...adminConfigItem} />}
        {/* Settings with user avatar - last item */}
        {settingsItem && (
          <SidebarItem
            icon={SettingsAvatarIcon}
            to={settingsItem.href}
            text={settingsItem.title}
          />
        )}
      </Sidebar>
    </SidebarPage>
  );
};

export default SidebarContent;
